<template>
  <div class="filters-actions">
    <div
      class="open-filter-modal"
      @click="showModal = true"
    >
      <v-icon
        class="plus-icon"
        icon="$plus"
        :size="20"
      />
      Custom Filters
      <FilterInfoTooltip
        v-if="tooltip.body_r"
        :tooltip="tooltip"
      />
      <AppliedFiltersDot
        v-if="customFiltersApplied"
        class="ml-2"
      />
    </div>
    <button
      v-if="customFiltersApplied"
      type="button"
      class="clear-button"
      @click="clearAllCustomFilters"
    >
      Clear
    </button>
  </div>
  <Modal
    :show="showModal"
    width="large"
    hide-default-actions
    @close="handleCloseModal"
  >
    <template #title>
      <div class="d-flex align-center">
        Custom Filters
        <FilterInfoTooltip
          v-if="tooltip.body_r"
          class="ml-1"
          :tooltip="tooltip"
          tooltip-placement="right"
        />
      </div>
    </template>
    <template #content>
      <div class="modal-content">
        <CustomFilterItems
          :filters="filters"
          :original-filters="originalFilters"
          @add-snackbar="emit('add-snackbar', $event)"
          @set-temp-filters="emit('set-temp-filters', $event)"
          @clear-filters="emit('clear-filters', $event)"
          @reset-values="emit('resetValues', $event)"
        />
      </div>
    </template>
    <template #footer>
      <div class="modal-footer">
        <button
          v-if="customFiltersApplied"
          type="button"
          class="clear-button mr-4"
          @click="clearAllCustomFilters"
        >
          Clear all
        </button>
        <DpplNewButton
          color="primary"
          size="large"
          class="apply-button w-100"
          @click="applyFilters"
        >
          Apply all
        </DpplNewButton>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import Modal from '@/components/shared/Modal.vue';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import CustomFilterItems from '@/components/filter/customFilters/CustomFilterItems.vue';
import AppliedFiltersDot from '@/components/filter/AppliedFiltersDot.vue';
import FilterInfoTooltip from '@/components/filter/FilterInfoTooltip.vue';

const router = useRouter();

const props = defineProps({
  filters: {
    type: Array,
    default: () => [],
  },
  originalFilters: {
    type: Array,
    default: () => [],
  },
  tooltip: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits([
  'set-temp-filters',
  'add-snackbar',
  'clear-filters',
  'apply-filters',
  'clear-multiple-filters',
  'resetValues',
  'resetMultipleValues',
]);

const showModal = ref(false);

const customFiltersApplied = computed(() => props.filters
  .map((filter) => filter.originalIndex)
  .some((filterIdx) => props.originalFilters[filterIdx].value.length));

const applyFilters = () => {
  showModal.value = false;
  emit('apply-filters');
};

const clearAllCustomFilters = () => {
  const allAppliedCustomFilters = props.filters
    .map(
      (filter) => (filter.value.length ? filter.originalIndex : null),
    )
    .filter(Boolean);

  emit('clear-multiple-filters', { indices: allAppliedCustomFilters, router });
};

const handleCloseModal = () => {
  showModal.value = false;

  const indices = props.filters
    .map((filter) => (filter.value.length ? filter.originalIndex : null))
    .filter(Boolean);

  emit('resetMultipleValues', { indices, setToAppliedFilters: true });
};
</script>

<style lang="less" scoped>
.filters-actions {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: @text-brand;
  padding: 0 8px;
  font-weight: 500;

  .open-filter-modal {
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    font-size: 13px;

    &:hover {
      cursor: pointer;
    }
  }

  :deep(.plus-icon) path {
    fill: @icon-brand;
  }
}

.clear-button {
  font-size: 12px;
  font-weight: 500;
  color: @text-brand;

  &:hover {
    cursor: pointer;
  }
}

.modal-content {
  height: 400px;
  max-height: 400px;
  overflow: auto;
  padding-top: 6px;
  padding-bottom: 6px;
  margin-bottom: 32px;
}

.modal-footer {
  position: absolute;
  bottom: 0px;
  width: 100%;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  background-color: @bg-default;
  padding: 12px 24px 12px 24px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.apply-button {
  width: auto !important;

  &.v-btn.v-btn--density-default {
    height: 32px !important;
  }
}
</style>
